import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ProjectCommunicationService } from '../../communication/project-comms.service';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { Feedstock } from '../../models/project/feedstock/feedstock.model';
import Utils from '../../util';
import { ConfiguredToastrService } from '../../helper/toastr/configured-toastr.service';
import { FeedstockBmpType } from '../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential.model';
import { FeedstockBmpPotentialDirect } from '../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-direct.model';
import { FeedstockBmpPotentialYield } from '../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-yield.model';
import { FeedstockBmpPotentialCod } from '../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-cod.model';
import { FeedstockBmpPotentialNutrient } from '../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-nutrient.model';
import { FeedstockBmpPotentialElemental } from '../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-elemental.model';
import { FeedstockMaterial } from '../../models/data-structures/materials/materials-data.model';
import { UnitValue } from '../../models/data-structures/unit-value.model';
import { Unit, UnitService } from '../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { UnitValueValidation } from '../../helper/validation/validation-sub-modules/unit-value.validation';
import { AnessaTranslationService } from '../../helper/translation/anessa-translation.service';
import { FeedstockBmpPotentialAI1 } from '../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-AI1.model';
import { FeedstockBmpPotentialAI2 } from '../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-AI2.model';


@Injectable({
  providedIn: 'root'
})
export class FeedstockService {
  /** A notifier that is emitted when this component is destroyed */
  onDestroyNotifier = new EventEmitter();
  /** An array of the feedstocks from the current project */
  feedstocks: Feedstock[];
  /** A notifier that is emitted when feedstocks ar loaded  */
  feedstockServiceReadyNotifier = new BehaviorSubject<boolean>(false);
  /** A notifier that is emitted when feedstocks are saved */
  feedstocksSavedNotifier = new BehaviorSubject<boolean>(false);
  /** A notifier that will be emitted either of the cod inputs are changed. This emitter is used for keeping the
   * cod values the same in the characteristics and bmp section */
  codChangedNotifier = new EventEmitter<{ origin: string, uv: UnitValue}>();
  /** A notifier that will be emitted when the database material changes. This emitter is used for autofilling
   * values in the characteristics and bmp section */
  materialSelectedNotifier = new BehaviorSubject<FeedstockMaterial>(null);
  /**
   * Boolean that determines if the project service has been loaded;
   */
  projectServiceLoaded = false;

  /**
   * A notifier for when inputs in the characteristics are changed. This subject is used to make
   * the feedstock-bmp-ai1 and feedstock-bmp-ai2 components aware of
   * the change in the `vs` (volatile solid) value
   */
  characteristicsChangedNotifier = new BehaviorSubject<boolean>(false);

  /**
   * loads feedstocks when the project is loaded. Feedstocks are also unloaded when the project
   * is unloaded
   * @param projectCommService is an instance of the ProjectCommunicationService
   * @param toastrService is an instance of the ConfiguredToastrService
   * @param unitService
   */
  constructor(private projectCommService: ProjectCommunicationService,
              private toastrService: ConfiguredToastrService,
              private unitService: UnitService,
              public translationService: AnessaTranslationService
  ) {
    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        if (loaded) {
          this.feedstocks = this.projectCommService.getFeedstocks();
        } else {
          this.feedstocks = null;
        }
        this.updatedNotifier();
        this.projectServiceLoaded = loaded;
      });

    this.projectCommService.projectSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(saved => {
        if (saved) {
          this.feedstocks = this.projectCommService.getFeedstocks();
          this.feedstocksSavedNotifier.next(true);
        }
        this.updatedNotifier();
      });

    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
          this.updatedNotifier();
        }
      );
  }

  updatedNotifier() {
    this.feedstockServiceReadyNotifier.next(
      this.projectCommService.projectFullyLoadedNotifier.value &&
      this.unitService.unitsLoadedNotifier.value
    );
  }

  /**
   * Saves all feedstocks to the project, which in turn saves the project and feedstocks to the DB
   * @param resetCapexOpex is a boolean that determines if capex and opex should be reset during the save
   */
  save(resetCapexOpex = true) {
    this.projectCommService.saveFeedstocks(this.feedstocks, resetCapexOpex);
    this.feedstocksSavedNotifier.next(true);
  }

  getDecimalPrecision() {
    return this.projectCommService.getPrecision();
  }

  getProjectDefaults() {
    return this.projectCommService.getProjectDefaults();
  }

  getWasteDisposalIncluded() {
    const ghg = this.projectCommService.getProjectGhgEmissions();
    const projInfo = this.projectCommService.getProjectInfo();
    // not user errors, no translations needed
    if (!ghg) {
      Error('unable to get ghg emission');
    }
    if (!projInfo) {
      Error('unable to get project info');
    }
    return ghg.includeWasteDisposal && projInfo.useGHG;
  }

  /**
   * Returns a separate instance (copy) of the feedstocks array
   */
  getFeedstocks() {
    if (this.feedstocks) {
      return Feedstock.arrayDeepCopy(this.feedstocks);
    }
    throw Error(this.translationService.get('SERVICE.FEEDSTOCK.CANT_GET_FEEDSTOCKS'));
  }

  /**
   * Returns a separate instance (copy) of a specific feedstock.
   * @param index is the index in the feedstock array at which the target feedstock is located
   */
  getFeedstock(index: number) {
    if (this.feedstocks && Utils.isNumeric(index)) {
      if (index > -1 && index < this.feedstocks.length) {
        return Feedstock.deepCopy(this.feedstocks[index]);
      }
    }
    throw Error(this.translationService.get('SERVICE.FEEDSTOCK.CANT_GET_FEEDSTOCK'));
  }

  /**
   * Returns the feedstock with the provided name
   * NOTE: all feedstocks are required to have unique names
   * @param name is the name of the required feedstock
   */
  getFeedstockByName(name: string) {
    if (this.feedstocks && name) {
      return this.feedstocks.findIndex(feedstock => feedstock.name === name);
    }
    throw Error(this.translationService.get('SERVICE.FEEDSTOCK.CANT_FIND_FEEDSTOCK') + ' \'' + name + '\'');
  }

  /**
   * Adds the provided feedstock to the projects feedstocks and saves the project
   * @param feedstock is the feedstock that wil be added and saved to the project
   * @param index is the index of the provided feedstock to be added in the array of feedstocks
   */
  addFeedstock(feedstock: Feedstock, index?: number) {
    if (this.feedstocks && feedstock) {
      if (Utils.nameIsUnique(feedstock.name, this.feedstocks)) {
        if (!feedstock.characteristics) throw Error(this.translationService.get('SERVICE.FEEDSTOCK.CHARACTERISTICS_UNAVAILABLE'));
        this.normalizeDensityEnabled(feedstock.characteristics.densityEnabled);
        this.normalizeCodEnabled(feedstock.characteristics.codEnabled);
        this.normalizeNpkEnabled(feedstock.npkEnabled);
        this.sanitizeFeedstock(feedstock);
        if (typeof index !== 'undefined') {
          this.feedstocks.splice(index, 0, feedstock);
        } else {
          this.feedstocks.push(feedstock);
        }
        this.save();
        return true;
      } else {
        this.toastrService.error(this.translationService.get('SERVICE.FEEDSTOCK.FEEDSTOCK_EXISTS'), this.translationService.get('SERVICE.FEEDSTOCK.SAVE_UNSUCCESSFUL'));
      }
    }
  }

  /**
   * Saves the provided feedstock at the provided index
   * @param feedstock and any changes made to it will be saved to the project at the provided index
   * @param index is the index of the provided feedstock in the array of feedstocks
   */
  saveFeedstock(feedstock: Feedstock, index: number) {
    if (this.feedstocks && feedstock && Utils.isNumeric(index)) {
      if (index > -1 && index < this.feedstocks.length) {
        if (Utils.nameIsUnique(feedstock.name, this.feedstocks, index)) {
          if (!feedstock.characteristics) throw Error(this.translationService.get('SERVICE.FEEDSTOCK.CHARACTERISTICS_UNAVAILABLE'));
          this.normalizeDensityEnabled(feedstock.characteristics.densityEnabled);
          this.normalizeCodEnabled(feedstock.characteristics.codEnabled);
          this.normalizeNpkEnabled(feedstock.npkEnabled);
          this.sanitizeFeedstock(feedstock);
          this.feedstocks[index] = feedstock;
          this.save();
          return true;
        } else {
          this.toastrService.error(this.translationService.get('SERVICE.FEEDSTOCK.FEEDSTOCK_EXISTS'), this.translationService.get('SERVICE.FEEDSTOCK.SAVE_UNSUCCESSFUL'));
          return;
        }
      }
    }
    throw Error(this.translationService.get('SERVICE.FEEDSTOCK.CANT_GET_FEEDSTOCK'));
  }

  /**
   * Save a feedstocks financial data; this doesn't reset capex and opex, unlike the general
   * save feedstock functionality
   * @param feedstock and any changes made to it's financial data will be saved to the project at the provided
   * index
   * @param index is location of the feedstock in the feedstock array
   */
  saveFeedstockFinancial(feedstock: Feedstock, index: number) {
    if (this.feedstocks && feedstock && Utils.isNumeric(index)) {
      if (index > -1 && index < this.feedstocks.length) {
        this.feedstocks[index] = feedstock;
        this.save(false);
        return;
      }
    }
    throw Error(this.translationService.get('SERVICE.FEEDSTOCK.CANT_SAVE_FINANCIAL'));
  }

  /**
   * Removes the target feedstock from the project structure and saves the project
   * @param index is the location of the target feedstock
   */
  deleteFeedstock(index: number) {
    if (this.feedstocks && Utils.isNumeric(index)) {
      if (index > -1 && index < this.feedstocks.length) {
        this.feedstocks.splice(index, 1);
        this.save();
      }
    }
  }

  /**
   * Get the feedstock mass, in tonne, per year.
   * @param f is a Feedstock object.
   */
  getNormalizedMassPerYear(f: Feedstock): number {
    const mass: UnitValue = f.quantities.getMass();
    const time: UnitValue = f.quantities.period;

    if (!new UnitValueValidation(mass).isValid() || !new UnitValueValidation(time).isValid()) {
      return 0;
    }

    const massInKG = this.unitService.getValueInBaseUnit(mass);
    const timeInDays = this.unitService.getValueInBaseUnit(time);

    return (massInKG / 1000) / (timeInDays / 365);
  }

  getNormalizedMassPerYearInDefaultUnit(f: Feedstock, useImperial = false) {
    if (this.defaultFeedstockUnitIsImperial() || useImperial) {
      const tonne = this.unitService.getUnitById('000000000000000000001203');
      const shortTon = this.unitService.getUnitById('000000000000000000001204');
      return Unit.convertValue(this.getNormalizedMassPerYear(f), tonne.id, shortTon.id);
    }
    return this.getNormalizedMassPerYear(f);
  }

  getNormalizedMassPerYearForUnitType(f: Feedstock, useImperial: boolean) {
    if (useImperial) {
      const tonne = this.unitService.getUnitById('000000000000000000001203');
      const shortTon = this.unitService.getUnitById('000000000000000000001204');
      return Unit.convertValue(this.getNormalizedMassPerYear(f), tonne.id, shortTon.id);
    }
    return this.getNormalizedMassPerYear(f);
  }

  getNormalizedTotalMassPerYear() {
    let totalMass = 0;
    for (let i = 0; i < this.feedstocks?.length; i++) {
      const feedstock = this.feedstocks[i];
      totalMass += this.getNormalizedMassPerYear(feedstock);
    }
    return totalMass;
  }

  /**
   * Get the feedstock mass, in tonne, per day.
   * @param f is a Feedstock object.
   */
  getNormalizedMassPerDay(f: Feedstock): number {
    const mass: UnitValue = f.quantities.getMass();
    const time: UnitValue = f.quantities.period;

    const massInKg = this.unitService.getValueInBaseUnit(mass);
    const timeInDays = this.unitService.getValueInBaseUnit(time);

    return (massInKg / 1000) / timeInDays;
  }

  /**
   * Get a string for the price amount per ton of the provided feedstock
   * @param feedstock is a Feedstock object.
   */
  getNormalizedPricePerTon(feedstock: Feedstock): string {
    return Utils.addCommas(this.getNormalizedPricePerTonValue(feedstock));
  }

  /**
   * Get the the price amount per ton of the provided feedstock
   * @param feedstock is a Feedstock object.
   */
  getNormalizedPricePerTonValue(feedstock: Feedstock): number {
    const price = feedstock.financial.price;
    const mass = feedstock.financial.amount;

    const massInKG = this.unitService.getValueInBaseUnit(mass);

    return Utils.roundToPrecision((price.value / (massInKG / 1000)), 2);
  }

  /**
   * Get the price per tonne of the provided feedstock's transportation
   * @param feedstock is a Feedstock object
   */
  getNormalizedTransportPricePerTonValue(feedstock: Feedstock) {
    if (!feedstock || !feedstock.financial) {
      throw Error(this.translationService.get('SERVICE.FEEDSTOCK.CANT_PROCESS'));
    }
    const price = feedstock.financial.transportPrice;
    const mass = feedstock.financial.transportAmount;

    const massInKg = this.unitService.getValueInBaseUnit(mass);

    return Utils.roundToPrecision((price.value / (massInKg / 1000)), 2);
  }

  /**
   * Return a boolean for if the current selected digester type is dry batch
   */
  digesterTypeIsDryBatch() {
    const plantSpec = this.projectCommService.getProjectPlantSpecifications();
    if (!plantSpec) throw Error(this.translationService.get('SERVICE.FEEDSTOCK.PLANT_SPEC_UNAVAILABLE'));
    return plantSpec.isDryBatch();
  }

  getDensityToggle() {
    if (this.feedstocks && this.feedstocks[0] && this.feedstocks[0].characteristics) {
      return this.feedstocks[0].characteristics.densityEnabled;
    }
    return false;
  }

  defaultFeedstockUnitIsImperial() {
    const defaultUnits = this.projectCommService.getProjectDefaults();
    if (!defaultUnits) {
      throw Error(this.translationService.get('SERVICE.FEEDSTOCK.DEFAULTS_UNAVAILABLE'));
    }
    if (defaultUnits.solid === '000000000000000000001202' || defaultUnits.solid === '000000000000000000001204') {
      return true;
    } else if (defaultUnits.solid) {
      return false;
    } else {
      return undefined;
    }
  }

  anyFeedstockUnitIsImperial() {
    for (const feed of this.feedstocks) {
      if (feed.quantities.mass.unit === '000000000000000000001202' || feed.quantities.mass.unit === '000000000000000000001204') {
        return true;
      }
    }
    return false;
  }

  getCharacteristicsToggle() {
    if (this.feedstocks && this.feedstocks[0] && this.feedstocks[0].characteristics) {
      return this.feedstocks[0].characteristics.characteristicsEnabled;
    }
    return true;
  }

  /**
   * Disables/enables COD for all feedstocks depending on the provided activated parameter.
   * @param activated is a boolean that determines the state of COD enabled.
   */
  private normalizeCodEnabled(activated: boolean) {
    if (this.feedstocks) {
      for (const feedstock of this.feedstocks) {
        if (feedstock.characteristics) {
          feedstock.characteristics.codEnabled = activated;
        }
      }
    }
  }

  getNPKToggle() {
    if (this.feedstocks && this.feedstocks[0]) {
      return this.feedstocks[0].npkEnabled;
    }
    return false;
  }

  getCODToggle() {
    if (this.feedstocks && this.feedstocks[0] && this.feedstocks[0].characteristics) {
      return this.feedstocks[0].characteristics.codEnabled;
    }
    return false;
  }

  /**
   * Disables/enables density for all feedstocks depending on the provided activated parameter but only
   * if the current digester type is dry batch.
   * @param activated a boolean that determines the state of density enabled.
   */
  private normalizeDensityEnabled(activated: boolean) {
    if (this.feedstocks) {
      for (const feedstock of this.feedstocks) {
        if (feedstock.characteristics) {
          feedstock.characteristics.characteristicsEnabled = feedstock.characteristics.characteristicsEnabled || activated;
          feedstock.characteristics.densityEnabled = activated;
        }
      }
    }
  }

  /**
   * Disabled/enabled NPK for all feedstocks depending on the provided activated parameter.
   * @param activated
   * @private
   */
  private normalizeNpkEnabled(activated: boolean) {
    if (this.feedstocks) {
      for (const feedstock of this.feedstocks) {
        feedstock.npkEnabled = activated;
      }
    }
  }

  /**
   * Cleans up any data in the feedstock obj that isn't necessary. Unnecessary data might consist of data entered
   * for a BMP model that isn't selected anymore, or data entered for the opposite material field
   * @param feedstock is a feedstock that will have all its unnecessary data removed
   */
  private sanitizeFeedstock(feedstock: Feedstock) {
    if (feedstock) {
      if (feedstock.quantities) this.sanitizeQuantities(feedstock);
      if (feedstock.bmpPotential) this.sanitizeBmpPotential(feedstock);
    }
  }

  /**
   * If the provided feedstock's material is of type animals mass will be reset, otherwise numAnimals and
   * manureProduction will be reset
   * @param feedstock is a feedstock that will have it's unnecessary feedstock quantities removed
   */
  private sanitizeQuantities(feedstock: Feedstock) {
    if (!feedstock || !feedstock.quantities) {
      throw Error(this.translationService.get('SERVICE.FEEDSTOCK.INVALID_FEED_QUANT_SANITIZE'));
    }
    if (feedstock.quantities.isAnimal) {
      feedstock.quantities.mass = new UnitValue();
    } else {
      feedstock.quantities.numAnimals = new UnitValue();
      feedstock.quantities.manureProduction = new UnitValue();
    }
  }

  /**
   * Clears all Bmp Potential models except the one selected
   * @param feedstock that will have its non-selected bmp models selected
   */
  private sanitizeBmpPotential(feedstock: Feedstock) {
    if (!feedstock || !feedstock.bmpPotential) {
      throw Error(this.translationService.get('SERVICE.FEEDSTOCK.INVALID_FEED_BMP_SANITIZE'));
    }
    // if (feedstock.bmpPotential.type !== FeedstockBmpType.BMP_DIRECT) {
    //   if (feedstock.bmpPotential.bmpDirect) feedstock.bmpPotential.bmpDirect = new FeedstockBmpPotentialDirect();
    // }
    // if (feedstock.bmpPotential.type !== FeedstockBmpType.BIOGAS_YIELD) {
    //   if (feedstock.bmpPotential.bmpYield) feedstock.bmpPotential.bmpYield = new FeedstockBmpPotentialYield();
    // }
    // if (feedstock.bmpPotential.type !== FeedstockBmpType.COD) {
    //   if (feedstock.bmpPotential.bmpCod) feedstock.bmpPotential.bmpCod = new FeedstockBmpPotentialCod();
    // }
    // if (feedstock.bmpPotential.type !== FeedstockBmpType.NUTRIENT_COMPOSITION) {
    //   if (feedstock.bmpPotential.bmpNutrient) feedstock.bmpPotential.bmpNutrient = new FeedstockBmpPotentialNutrient();
    // }
    // if (feedstock.bmpPotential.type !== FeedstockBmpType.ELEMENTAL) {
    //   if (feedstock.bmpPotential.bmpElemental) feedstock.bmpPotential.bmpElemental = new FeedstockBmpPotentialElemental();
    // }
    // if (feedstock.bmpPotential.type !== FeedstockBmpType.AI1) {
    //   if (feedstock.bmpPotential.bmpAI1) feedstock.bmpPotential.bmpAI1 = new FeedstockBmpPotentialAI1();
    // }
    // if (feedstock.bmpPotential.type !== FeedstockBmpType.AI2) {
    //   if (feedstock.bmpPotential.bmpAI2) feedstock.bmpPotential.bmpAI2 = new FeedstockBmpPotentialAI2();
    // }
  }
}
